import { useEffect, useState, useCallback, useMemo } from "react";
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from "react-joyride";
import { useLocation } from "wouter";
import { useBrokerTour } from "./BrokerTourManager";
import { brokerTourSteps, TourStepExtended } from "./tourSteps";
import { tourStyles, TOUR_CONFIG } from "./tourConfig";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";

export function BrokerTour() {
  const { isTourRunning, stopTour, completeTour, skipTour } = useBrokerTour();
  const [location, setLocation] = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const { hasFeature } = useSubscriptionTier();

  // Filter out steps that require features the user's tier doesn't have
  const filteredSteps = useMemo(() => {
    return brokerTourSteps.filter((step) => {
      if (!step.requiredFeature) return true;
      return hasFeature(step.requiredFeature);
    });
  }, [hasFeature]);

  // Handle navigation requirements for current step
  useEffect(() => {
    if (!isTourRunning || isNavigating) return;

    const currentStep = filteredSteps[stepIndex] as TourStepExtended;
    if (currentStep?.requiresNavigation) {
      const { path } = currentStep.requiresNavigation;
      
      // Only navigate if we're not already on the required path
      if (location !== path) {
        setIsNavigating(true);
        setLocation(path);
        
        // Give the page time to render before showing the next step
        setTimeout(() => {
          setIsNavigating(false);
        }, 500);
      }
    }
  }, [stepIndex, isTourRunning, location, setLocation, isNavigating, filteredSteps]);

  // Handle tour callbacks
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, action, index, type } = data;

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Move to next step
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      
      if (action === ACTIONS.NEXT && nextIndex < filteredSteps.length) {
        setStepIndex(nextIndex);
      } else if (action === ACTIONS.NEXT && nextIndex >= filteredSteps.length) {
        // We've reached the end of the tour
        completeTour();
        setStepIndex(0);
        return;
      } else if (action === ACTIONS.PREV && nextIndex >= 0) {
        setStepIndex(nextIndex);
      }
    }

    if (status === STATUS.FINISHED) {
      // Tour completed
      completeTour();
      setStepIndex(0);
    } else if (status === STATUS.SKIPPED) {
      // Tour skipped
      skipTour();
      setStepIndex(0);
    } else if (action === ACTIONS.CLOSE) {
      // Tour closed
      stopTour();
      setStepIndex(0);
    }
  }, [completeTour, skipTour, stopTour, filteredSteps.length]);

  // Reset step index when tour starts
  useEffect(() => {
    if (isTourRunning) {
      setStepIndex(0);
    }
  }, [isTourRunning]);

  if (!isTourRunning) {
    return null;
  }

  return (
    <Joyride
      steps={filteredSteps}
      stepIndex={stepIndex}
      run={isTourRunning && !isNavigating}
      callback={handleJoyrideCallback}
      continuous={TOUR_CONFIG.continuous}
      showProgress={TOUR_CONFIG.showProgress}
      showSkipButton={TOUR_CONFIG.showSkipButton}
      disableCloseOnEsc={TOUR_CONFIG.disableCloseOnEsc}
      disableOverlayClose={TOUR_CONFIG.disableOverlayClose}
      spotlightClicks={TOUR_CONFIG.spotlightClicks}
      hideBackButton={TOUR_CONFIG.hideBackButton}
      locale={TOUR_CONFIG.locale}
      styles={tourStyles}
      scrollToFirstStep
      scrollOffset={100}
    />
  );
}

